/* eslint-disable react/prop-types */
import { Button, Col, Form, Modal, Row } from "react-bootstrap"
import { useDispatch, useSelector } from "react-redux"
import { addContact, editContact, inviteContact } from "../../../data/contactSlice"
import Loader from "../../../components/Loader"
import { useEffect, useState } from "react"
import { css } from "@emotion/react"
import { validEmailRegex } from "../../../constant/utils"
import { PatternFormat } from "react-number-format"

const componentStyled = css`
    .footer-button-container {
        display: flex;
        justify-content: flex-end;
    }

    .footer-error-container {
        display: flex;
        justify-content: flex-end;
        color: red;
        margin-top: 6px;
    }
`

const AddEditUser = ({
    show,
    hide,
    currentUser,
    setCurrentUser,
    updateUser,
    getAgenciesList,
    contactAgencyId,
    switchingAgency
}) => {
    const dispatch = useDispatch()
    const { data: agencies, fetchStatus } = useSelector((state) => state.agency)
    const { fetchStatus: contactFetchStatus } = useSelector((state) => state.contact)
    const emailErrorMessage = "Please enter a valid email address"
    const [errorMessage, setErrorMessage] = useState("")

    useEffect(() => {
        if (show) setErrorMessage("")
    }, [show])

    useEffect(() => {
        if (!currentUser?.id && show) {
            const currentUserTemp = { ...currentUser }
            currentUserTemp.active = true
            currentUserTemp.sendInvite = true
            setCurrentUser({ ...currentUserTemp })
        }
    }, [show])

    useEffect(() => {
        if (!currentUser?.id && contactAgencyId) {
            setCurrentUser({ ...currentUser, brokerageId: contactAgencyId })
        }
    }, [contactAgencyId])

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target
        if (type === "checkbox") {
            setCurrentUser({ ...currentUser, [name]: checked })
        } else {
            setCurrentUser({ ...currentUser, [name]: value })
        }
    }

    const validateData = (data) => {
        if (
            !data?.brokerageId ||
            !data?.email ||
            !data?.firstName ||
            !data?.lastName ||
            !data?.phoneNumber ||
            !data?.producerCode
        ) {
            setErrorMessage("Please fill all required fields")
            return false
        } else if (!validEmailRegex.test(data?.email)) {
            setErrorMessage(emailErrorMessage)
            return false
        } else {
            return true
        }
    }

    const handleSave = () => {
        const { sendInvite, ...userPayload } = currentUser
        if (validateData(userPayload)) {
            if (userPayload?.id) {
                dispatch(editContact({ ...userPayload })).then(({ payload }) => {
                    if (payload?.succeeded) {
                        getAgenciesList().then(() => {
                            if (switchingAgency) {
                                hide(true)
                            }
                            hide(false)
                        })
                        if (sendInvite) {
                            dispatch(inviteContact(payload?.id))
                        }
                    } else {
                        hide(false)
                    }
                })
            } else {
                dispatch(addContact({ ...userPayload })).then(({ payload }) => {
                    if (payload?.succeeded) {
                        getAgenciesList().then(() => {
                            hide(false)
                        })
                        if (sendInvite) {
                            dispatch(inviteContact(payload?.id))
                        }
                    } else if (!!payload?.errorMessage) {
                        setErrorMessage(payload?.errorMessage)
                    } else {
                        hide(false)
                    }
                })
            }
        }
    }

    const hasError = (value, isEmail) => {
        if (errorMessage === emailErrorMessage && isEmail) {
            return !validEmailRegex.test(value)
        } else if (!!errorMessage && value) {
            return false
        } else if (!errorMessage) {
            return false
        } else return true
    }

    return (
        <Modal
            show={show}
            onHide={hide}
            size="lg"
            centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    {switchingAgency
                        ? "Update User Information to Reassign Broker Contacts"
                        : currentUser?.id
                        ? "Edit Contact"
                        : "Add Contact"}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Loader show={fetchStatus === "loading" || contactFetchStatus === "loading"} />
                <Form>
                    {!switchingAgency && (
                        <Form.Group
                            controlId="userRol"
                            className="mt-2">
                            <Form.Label>
                                Agency <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Select
                                className={`form-select-sm ${hasError(currentUser?.brokerageId) ? "error" : ""}`}
                                name="brokerageId"
                                value={currentUser?.brokerageId || ""}
                                onChange={handleChange}>
                                <option value={""}>--- Select ---</option>
                                {agencies?.map((agency) => (
                                    <option
                                        key={agency?.id}
                                        value={agency?.id}>
                                        {agency?.name || ""}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    )}

                    <Form.Group
                        className="mt-2"
                        controlId="producerCode">
                        <Form.Label>
                            Un-Admitted Producer Code <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            name="producerCode"
                            className={`form-select-sm ${hasError(currentUser?.producerCode) ? "error" : ""}`}
                            value={currentUser?.producerCode || ""}
                            onChange={handleChange}
                            maxLength={6}
                        />
                    </Form.Group>
                    {!switchingAgency && (
                        <Form.Group
                            className="mt-2"
                            controlId="nyftzProducerCode">
                            <Form.Label>NYFTZ Producer Code</Form.Label>
                            <Form.Control
                                type="text"
                                name="nyftzProducerCode"
                                className={`form-select-sm`}
                                value={currentUser?.nyftzProducerCode || ""}
                                onChange={handleChange}
                                maxLength={6}
                            />
                        </Form.Group>
                    )}
                    {!switchingAgency && (
                        <Form.Group
                            className="mt-2"
                            controlId="admittedProducerCode">
                            <Form.Label>Admitted Code</Form.Label>
                            <Form.Control
                                type="text"
                                name="admittedProducerCode"
                                className={`form-select-sm`}
                                value={currentUser?.admittedProducerCode || ""}
                                onChange={handleChange}
                                maxLength={6}
                            />
                        </Form.Group>
                    )}

                    {!switchingAgency && (
                        <>
                            <Form.Group
                                className="mt-2"
                                controlId="firstName">
                                <Form.Label>
                                    First Name <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    className={`form-select-sm ${hasError(currentUser?.firstName) ? "error" : ""}`}
                                    name="firstName"
                                    value={currentUser?.firstName || ""}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                            <Form.Group
                                controlId="lastName"
                                className="mt-2">
                                <Form.Label>
                                    Last Name <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    className={`form-select-sm ${hasError(currentUser?.lastName) ? "error" : ""}`}
                                    name="lastName"
                                    value={currentUser?.lastName || ""}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                        </>
                    )}

                    <Form.Group
                        controlId="phoneNumber"
                        className="mt-2">
                        <Form.Label>
                            Mobile Phone Number <span className="text-danger">*</span>
                        </Form.Label>
                        <PatternFormat
                            format="(###) ###-####"
                            allowEmptyFormatting
                            mask="_"
                            className={`form-control form-control-sm pattern-format ${
                                hasError(currentUser?.phoneNumber) ? "error" : ""
                            }`}
                            name="phoneNumber"
                            value={currentUser?.phoneNumber || ""}
                            onChange={handleChange}
                        />
                    </Form.Group>

                    <Form.Group
                        controlId="email"
                        className="mt-2">
                        <Form.Label>
                            Email Address (Username) <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            type="email"
                            className={`form-select-sm ${hasError(currentUser?.email, true) ? "error" : ""}`}
                            name="email"
                            value={currentUser?.email || ""}
                            onChange={handleChange}
                        />
                    </Form.Group>
                    {currentUser?.id && !switchingAgency && (
                        <Row sm={12}>
                            <Col sm={2}>
                                <Form.Group
                                    controlId="formActive"
                                    className="mt-2">
                                    <Form.Check
                                        type="checkbox"
                                        label="Active"
                                        name="active"
                                        checked={currentUser?.active === true}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col sm={2}>
                                <Form.Group
                                    controlId="formActive"
                                    className="mt-2">
                                    <Form.Check
                                        type="checkbox"
                                        label="Send Invite"
                                        name="sendInvite"
                                        checked={currentUser?.sendInvite === true}
                                        onChange={handleChange}
                                        disabled={!currentUser?.active || currentUser?.active === false}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer css={componentStyled}>
                <Col sm={6}>
                    <div className="footer-button-container">
                        <Button
                            className="btn-sm me-2"
                            variant="secondary"
                            onClick={() => hide(false)}>
                            Cancel
                        </Button>
                        <Button
                            className="btn-sm"
                            variant="primary"
                            onClick={handleSave}>
                            Save
                        </Button>
                    </div>
                    {!!errorMessage && <div className="footer-error-container">{errorMessage}</div>}
                </Col>
            </Modal.Footer>
        </Modal>
    )
}

export default AddEditUser
